import React from 'react'
import {
   //@ts-ignore
   formatDiscountPercent,
   //@ts-ignore
   formatFloatAsCurrencySymbol,
} from '../common/helpers.mjs'
import type {
   //@ts-ignore
   CalculatedValues,
   SummarizedDiscount,
   //@ts-ignore
} from '../common/types/calculate'
//@ts-ignore
import type { currencyObject, Product } from '../common/types/types'

// Extended discount type that includes disclaimer fields added by GenerateQuoteData
interface DisclaimerDiscount extends SummarizedDiscount {
   Disclaimer?: string | null
   DisclaimerSymbol?: string
   StartMonth?: number | null
   EndMonth?: number | null
   total: number
   DiscountAmount: number
   ApplyTo: string
   DiscId: number
}

// Type for the selectedProductCosts values
interface ProductCost {
   totalPrice: number
   totalDiscount: number
}

interface FirstAsteriskProps {
   getString: (key: string, fallback?: string) => string
   hasPEPMCols: boolean
   allOverMin: boolean
}

interface SecondAsteriskProps {
   getString: (key: string, fallback?: string) => string
   mixedOverUnder25: boolean
}

interface TermContractDisclaimerProps {
   getFormattedString: (
      key: string,
      variables?: Record<string, string | number>,
      fallback?: string,
   ) => string
   termLength: number
   hrEECount: number
}

interface ATSOnlyDisclaimerProps {
   getString: (key: string, fallback?: string) => string
   isATSOnly: boolean
}

interface DueAtActivationAsteriskProps {
   mixedOverUnder25: boolean
   bundleApplied: boolean
   hasPayrollImp: boolean
   includePayrollImpInActivation: boolean
}

interface FoundationsPayrollImpTypeAsAsteriskProps {
   getFormattedString: (
      key: string,
      variables?: Record<string, string | number>,
      fallback?: string,
   ) => string
   payrollImpType: string
   mixedOverUnder25: boolean
}

interface DisclaimersProps {
   calculatedValues: CalculatedValues
   currencyObject: currencyObject
   product_list: Record<string, Product>
   payrollFirstPayDate?: string | null
}

interface CommentsProps {
   comments: string | null
}

export interface FootnotesProps {
   getString: (key: string, fallback?: string) => string
   getFormattedString: (
      key: string,
      variables?: Record<string, string | number>,
      fallback?: string,
   ) => string
   hasPEPMCols: boolean
   mixedOverUnder25: boolean
   comments: string | null
   allOverMin: boolean
   bundleApplied: boolean
   hasPayrollImp: boolean
   includePayrollImpInActivation: boolean
   calculatedValues: CalculatedValues
   payrollImpType: string
   currencyObject: currencyObject
   termLength: number
   hrEECount: number
   termContractPermissions: string[] | null
   userRole: string
   product_list: Record<string, Product>
   isCoTerm?: boolean
   coTermMonths?: number | null
   payrollFirstPayDate?: string | null
   isATSOnly: boolean
}

function replaceStringsInText(
   text: string,
   replacements: Record<string, string>,
): string {
   // Iterate through each replacement object
   Object.keys(replacements).forEach((key) => {
      // Replace all occurrences of the current key with its corresponding value
      text = text.replace(key, replacements[key])
   })
   return text
}

function FirstAsterisk({
   getString,
   hasPEPMCols,
   allOverMin,
}: FirstAsteriskProps) {
   if (hasPEPMCols || allOverMin) {
      return (
         <div>
            {getString(
               'quote.Footnotes.FirstAsterisk.Text',
               '* This is your monthly/extended term price based on the quoted headcount and products. This price reflects the minimum you will be billed for the employee headcount quoted above until that number is exceeded. If your total employee count increases, so will your total monthly cost.',
            )}
            <span className='infoItem'></span>
         </div>
      )
   } else {
      return (
         <div>
            {getString(
               'quote.Footnotes.FirstAsterisk.AltText',
               '* This is your monthly/extended term price based on the quoted headcount and products. Companies with 25 or fewer employees are charged a minimum flat rate. Once your company reaches 26 or more employees you will be charged on a per employee per month (PEPM) basis and will be eligible for volume discounts.',
            )}
            <span className='infoItem'></span>
         </div>
      )
   }
}

function SecondAsterisk({ getString, mixedOverUnder25 }: SecondAsteriskProps) {
   if (mixedOverUnder25) {
      return (
         <div>
            {getString('quote.Footnotes.SecondAsterisk.Text')}
            <span className='infoItem'></span>
         </div>
      )
   }
   return <></>
}

function ATSOnlyDisclaimer({ getString, isATSOnly }: ATSOnlyDisclaimerProps) {
   if (!isATSOnly) return <></>
   return (
      <div>
         <b>{getString('quote.Footnotes.ATSOnlyDisclaimer.Text')}</b>
         <span className='infoItem'></span>
      </div>
   )
}

function TermContractDisclaimer({
   getFormattedString,
   termLength,
   hrEECount,
}: TermContractDisclaimerProps) {
   if (termLength > 1 && hrEECount > 0) {
      return (
         <div>
            <b>
               {getFormattedString(
                  'quote.Footnotes.TermContractDisclaimer.Text',
                  { hrEECount },
               )}
            </b>
            <span className='infoItem'></span>
         </div>
      )
   }
   return <></>
}

function DueAtActivationAsterisk({
   mixedOverUnder25,
   bundleApplied,
   hasPayrollImp,
   includePayrollImpInActivation,
}: DueAtActivationAsteriskProps) {
   if (mixedOverUnder25) {
      return (
         <div>
            <b>
               *** Applicable sales tax may be added.
               {hasPayrollImp && !includePayrollImpInActivation && (
                  <>
                     {' '}
                     A separate Price Quote for the Payroll product &
                     implementation will be provided from a Payroll specialist.
                     Payment for Payroll implementation will be due immediately
                     upon the closing of the Payroll product.
                  </>
               )}
            </b>
            &nbsp;
            {bundleApplied && (
               <b>
                  The Bundle Discount will automatically apply any month you are
                  being actively billed for all three products: HR, Benefits
                  Administration, and Payroll.
               </b>
            )}
            <span className='infoItem'></span>
         </div>
      )
   } else {
      return (
         <div>
            <b>
               ** Applicable sales tax may be added.
               {hasPayrollImp && !includePayrollImpInActivation && (
                  <>
                     {' '}
                     A separate Price Quote for the Payroll product &
                     implementation will be provided from a Payroll specialist.
                     Payment for Payroll implementation will be due immediately
                     upon the closing of the Payroll product.
                  </>
               )}
            </b>
            &nbsp;
            {bundleApplied && (
               <b>
                  The Bundle Discount will automatically apply any month you are
                  being actively billed for all three products: HR, Benefits
                  Administration, and Payroll.
               </b>
            )}
            <span className='infoItem'></span>
         </div>
      )
   }
}

function FoundationsPayrollImpTypeAsAsterisk({
   getFormattedString,
   payrollImpType,
   mixedOverUnder25,
}: FoundationsPayrollImpTypeAsAsteriskProps) {
   if (payrollImpType === 'foundations') {
      return (
         <div>
            <b>
               {getFormattedString(
                  'quote.Footnotes.FoundationsPayrollImpTypeAsAsterisk.Text',
                  { asteriskNumber: mixedOverUnder25 ? '****' : '***' },
               )}
            </b>
            <span className='infoItem'></span>
         </div>
      )
   }
   return <></>
}

/**
 * Gets a comma-separated list of selected product names from apply_to codes
 * @param applyTo - Comma-separated product codes (e.g., "core1,core2,benAdmin")
 * @param selectedProductCosts - Object with product codes as keys
 * @param product_list - Object with product codes as keys containing ProductName
 * @returns Comma-separated list of selected product names
 */
function getSelectedProductNames(
   applyTo: string | undefined,
   selectedProductCosts: Record<string, ProductCost> | undefined,
   product_list: Record<string, Product> | undefined,
): string {
   if (!applyTo || !selectedProductCosts || !product_list) return applyTo || ''

   const productCodes = applyTo.split(',').map((code) => code.trim())

   const selectedNames = productCodes
      .filter((code) => {
         // Check if product is selected (exists in selectedProductCosts with totalPrice > 0 or totalDiscount > 0)
         const productCost = selectedProductCosts[code]
         return (
            productCost &&
            (productCost.totalPrice > 0 || productCost.totalDiscount > 0)
         )
      })
      .map((code) => {
         // Get product name from product_list, fallback to code if not found
         return product_list[code]?.ProductName || code
      })

   return selectedNames.length > 0 ? selectedNames.join(', ') : applyTo
}

function Disclaimers({
   calculatedValues,
   currencyObject,
   product_list,
   payrollFirstPayDate,
}: DisclaimersProps) {
   // Combines summarized monthly and onetime discounts
   const summarizedDiscounts = (
      calculatedValues.monthlyTotal.appliedDiscounts as DisclaimerDiscount[]
   ).concat(
      calculatedValues.oneTimeTotal.appliedDiscounts as DisclaimerDiscount[],
   )

   if (summarizedDiscounts && summarizedDiscounts.length > 0) {
      const formattedPayrollDate = payrollFirstPayDate
         ? (() => {
              const [y, m, d] = payrollFirstPayDate.split('-').map(Number)
              return new Date(y, m - 1, d).toLocaleDateString('en-US', {
                 month: 'numeric',
                 day: 'numeric',
                 year: 'numeric',
              })
           })()
         : ''

      const disclaimerElements = summarizedDiscounts
         .filter((discount) => {
            // Only display disclaimer if it's a non-null, non-undefined, non-empty string
            return (
               discount.Disclaimer &&
               typeof discount.Disclaimer === 'string' &&
               discount.Disclaimer.trim() !== ''
            )
         })
         .map((discount, index) => {
            // Key words to be replaced in the disclaimer
            const totalWithoutDiscount =
               //@ts-ignore
               calculatedValues.monthlyTotal.totalPrice + discount.total
            const replacements: Record<string, string> = {
               $PAYROLL_FIRST_PAY_DATE: formattedPayrollDate,
               $DISCOUNT_AMOUNT: `${formatFloatAsCurrencySymbol(discount.total, currencyObject)}`,
               $DISCOUNT_PERCENT: `${formatDiscountPercent(Number(discount.DiscountAmount))}`,
               $TOTAL_WITHOUT_DISCOUNT: `$${formatFloatAsCurrencySymbol(totalWithoutDiscount.toFixed(2), currencyObject)}`,
               $START_MONTH: String(discount.StartMonth ?? ''),
               $END_MONTH: String(discount.EndMonth ?? ''),
               $APPLIED_PRODUCTS: getSelectedProductNames(
                  discount.ApplyTo,
                  calculatedValues?.selectedProductCosts as Record<
                     string,
                     ProductCost
                  >,
                  product_list,
               ),
            }
            // Replace key words in the disclaimer
            const disclaimerText = replaceStringsInText(
               discount.Disclaimer as string,
               replacements,
            )
            return (
               <div key={`disclaimer-${discount.DiscId}-${index}`}>
                  {discount.DisclaimerSymbol} {disclaimerText}
                  <span className='infoItem'></span>
               </div>
            )
         })

      return <>{disclaimerElements}</>
   }
   return <></>
}

function Comments({ comments }: CommentsProps) {
   if (comments) {
      return (
         <span style={{ whiteSpace: 'pre-line' }}>
            <br />
            <br />
            {comments}
         </span>
      )
   }
   return <></>
}

//TODO: EASYBUTTON Remove termContractPermissions and userRole once we go live
export default function Footnotes({
   getString,
   getFormattedString,
   hasPEPMCols,
   mixedOverUnder25,
   comments,
   allOverMin,
   bundleApplied,
   hasPayrollImp,
   includePayrollImpInActivation,
   calculatedValues,
   payrollImpType,
   currencyObject,
   termLength,
   hrEECount,
   termContractPermissions,
   userRole,
   product_list,
   isCoTerm,
   coTermMonths,
   payrollFirstPayDate,
   isATSOnly,
}: FootnotesProps) {
   return (
      <div className='info avoid-break-after'>
         <FirstAsterisk
            getString={getString}
            hasPEPMCols={hasPEPMCols}
            allOverMin={allOverMin}
         />
         <SecondAsterisk
            getString={getString}
            mixedOverUnder25={mixedOverUnder25}
         />
         <Disclaimers
            calculatedValues={calculatedValues}
            currencyObject={currencyObject}
            product_list={product_list}
            payrollFirstPayDate={payrollFirstPayDate}
         />
         <DueAtActivationAsterisk
            mixedOverUnder25={mixedOverUnder25}
            bundleApplied={bundleApplied}
            hasPayrollImp={hasPayrollImp}
            includePayrollImpInActivation={includePayrollImpInActivation}
         />
         <ATSOnlyDisclaimer getString={getString} isATSOnly={isATSOnly} />
         <TermContractDisclaimer
            getFormattedString={getFormattedString}
            termLength={termLength}
            hrEECount={hrEECount}
         />
         <FoundationsPayrollImpTypeAsAsterisk
            getFormattedString={getFormattedString}
            payrollImpType={payrollImpType}
            mixedOverUnder25={mixedOverUnder25}
         />
         {isCoTerm && coTermMonths != null && (
            <div>
               {getFormattedString('quote.Footnotes.CoTermDisclaimer.Text', {
                  coTermMonths,
               })}
            </div>
         )}
         {/* TODO: EASYBUTTON Remove this comment once we go live */}
         {!(
            termContractPermissions &&
            termContractPermissions.includes(userRole)
         ) && <div>Month-to-Month | No Long-Term Commitment</div>}
         <Comments comments={comments} />
      </div>
   )
}
